import { useState, useEffect } from 'react';
import { validateEmail, validateUsername, filterEmailInput, hasInvalidUsernameChars } from '../utils/validation';
import styles from './ValidatedInput.module.css';

/**
 * Combined email/username input field for login
 * - Detects @ symbol to determine if input is email or username
 * - If @ present → validates as email with email-specific filtering
 * - If @ absent → validates as username with alphanumeric-only filtering
 * - Shows temporary tooltip when invalid character is typed
 */
export function EmailOrUsernameInput({
    value,
    onChange,
    error,
    onValidationChange,
    placeholder = 'Brugernavn eller Email',
    disabled = false,
    showValidationFeedback = true
}) {
    const [touched, setTouched] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false);
    const [inputType, setInputType] = useState('username'); // 'username' or 'email'

    // Determine if input is email or username based on @ symbol
    const isEmail = value.includes('@');

    // Validate on change
    useEffect(() => {
        if (value) {
            let result;
            if (isEmail) {
                result = validateEmail(value);
                setInputType('email');
            } else {
                // For login, we don't need the min 3 char requirement
                const validation = validateUsername(value);
                result = validation;
                setInputType('username');
            }

            setValidationResult(result);
            if (onValidationChange) {
                onValidationChange(result.valid);
            }
        } else {
            setValidationResult(null);
            setInputType('username');
            if (onValidationChange) {
                onValidationChange(false);
            }
        }
    }, [value, isEmail, onValidationChange]);

    useEffect(() => {
        if (!showInvalidCharTooltip) return;

        const timer = setTimeout(() => {
            setShowInvalidCharTooltip(false);
        }, 1500);

        return () => clearTimeout(timer);
    }, [showInvalidCharTooltip]);

    const handleChange = (e) => {
        let newValue = e.target.value;
        const oldValue = value;
        const willBeEmail = newValue.includes('@');

        if (willBeEmail) {
            // Treat as email - filter email characters
            const filtered = filterEmailInput(newValue, value);

            // If value was rejected (filtering removed characters), show tooltip
            if (filtered === oldValue && newValue !== oldValue) {
                setShowInvalidCharTooltip(true);
                return;
            }

            // Enforce max length for email
            newValue = filtered.slice(0, 255);
        } else {
            // Treat as username - filter alphanumeric only
            const hasInvalid = newValue
                .split('')
                .some(char => hasInvalidUsernameChars(char));

            // Filter out invalid characters
            newValue = newValue
                .split('')
                .filter(char => !hasInvalidUsernameChars(char))
                .join('');

            // Show tooltip if invalid character was typed
            if (hasInvalid && newValue === oldValue) {
                setShowInvalidCharTooltip(true);
                return;
            }

            // Enforce max length for username
            newValue = newValue.slice(0, 30);
        }

        onChange(newValue);
    };

    const handleBlur = () => {
        setTouched(true);
    };

    const showError = touched && validationResult && !validationResult.valid;
    const showSuccess = touched && validationResult && validationResult.valid;

    // Get appropriate tooltip message
    const getTooltipMessage = () => {
        if (isEmail) {
            return 'Tilladt: bogstaver, tal, . _ + -';
        }
        return 'Kun bogstaver og tal';
    };

    // Get max length for character counter
    const maxLength = isEmail ? 255 : 30;

    return (
        <div className={styles.fieldWrapper}>
            {showInvalidCharTooltip && (
                <div className={styles.tooltip}>
                    {getTooltipMessage()}
                </div>
            )}
            <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                className={`${styles.input} ${showError ? styles.inputError : ''} ${showSuccess ? styles.inputSuccess : ''}`}
                maxLength={maxLength}
                autoComplete="username"
            />
            {value && (
                <div className={styles.charCount}>
                    {value.length}/{maxLength}
                </div>
            )}
            {showError && (
                <div className={styles.errorMessage}>
                    ⚠️ {validationResult.error}
                </div>
            )}
            {showSuccess && showValidationFeedback && (
                <div className={styles.successMessage}>
                    ✓ {isEmail ? 'Email accepteret' : 'Brugernavn accepteret'}
                </div>
            )}
            {error && (
                <div className={styles.serverError}>
                    {error}
                </div>
            )}
        </div>
    );
}
